import { QuestionTypeSelect, QuestionSelectOption } from "../../types/gameSession";
import React, { ChangeEvent, useEffect, useState } from "react";
import { updateOptionAnswer, updateOptionSelected } from "../../app/slices/gameSession";
import { useAppDispatch, useAppSelector } from "../../app/store";

type Props = {
    question: QuestionTypeSelect;
    showPoints: boolean;
};

const QuestionSelect = ({ question, showPoints }: Props) => {
    const { data } = useAppSelector((state) => state.game);
    const displayCorrectAnswers = data?.display_correct_answers || false;

    const questionLayout: string = question.layout || "default";

    const renderOptions = question.options.map((option) =>
        option.option_type === "open" ? (
            <QuestionOptionOpen key={option.index} option={option} />
        ) : (
            <QuestionOptionBasic
                key={option.index}
                option={option}
                displayCorrectAnswers={displayCorrectAnswers}
                layout={questionLayout}
            />
        )
    );

    return (
        <>
            {question.image && (
                <div className='question-image-container'>
                    <img
                        src={question.image}
                        alt={question.title}
                        className='question-image'
                    />
                </div>
            )}
            <div className={`select-container ${questionLayout}`}>
                {renderOptions}
            </div>
        </>
    );
};

export default QuestionSelect;

const QuestionOptionBasic = ({
    option,
    displayCorrectAnswers,
    layout
}: {
    option: QuestionSelectOption;
    displayCorrectAnswers: boolean;
    layout: string;
}) => {
    const dispatch = useAppDispatch();

    const handleSelectOptionClick = (option: QuestionSelectOption) => {
        dispatch(
            updateOptionSelected({
                optionIndex: option.index,
                selected: !option.selected
            })
        );
    };
    const classNames = `question-option-select ${option.selected ? 'selected' : ''} ${displayCorrectAnswers ? (option.points > 0 ? 'correct' : 'incorrect') : ''
        } ${layout}`.trim();


    return (
        <div key={option.index} className={classNames} onClick={() => handleSelectOptionClick(option)}>
            <div className={`question-option-col`}>
                <div className="question-option-wrapper">
                    <span>{option.option}</span>
                </div>
                {displayCorrectAnswers && (
                    <div className="option-points-sm">
                        <span>
                            {option.points > 0 && '+'}
                            {option.points}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

const QuestionOptionOpen = ({ option }: { option: QuestionSelectOption }) => {
    const dispatch = useAppDispatch();
    const [inputValue, setInputValue] = useState(option.answer || "");

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
    };

    useEffect(() => {
        if (inputValue !== '') {
            const delayDebounceFn = setTimeout(() => {
                dispatch(updateOptionAnswer({
                    optionIndex: option.index,
                    answer: inputValue,
                    selected: !!inputValue
                }));
            }, 300);
            return () => clearTimeout(delayDebounceFn);
        }
    }, [inputValue, dispatch, option.index]);

    useEffect(() => {
        setInputValue(option.answer || "");
    }, [option.answer]);

    return (
        <div key={option.index} className="question-option-select-open ">
            <label className="form-label">{option.option}</label>
            <input
                type="text"
                className="form-control"
                value={inputValue}
                onChange={handleChange}
            />
        </div>
    );
}